import { useWatch, type Control } from "react-hook-form";
import { InfraAttributeKeyCombobox } from "@/components/inventory/infra-attribute-key-combobox";
import { InfraAttributeValueCombobox } from "@/components/inventory/infra-attribute-value-combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Storage shape on the form's `variants[]` field array for infra items.
// Replaces sellingPrice/currentStock with three nullable override fields.
// NULL on any of hsnCode/gstRate/unitPrice means "inherit parent item's value".
export interface InfraVariantRowValue {
  attributeKeyId: number | null;
  attributeValueId: number | null;
  attributeValueText?: string;
  itemCode?: string | null;
  displayName?: string | null;
  // Per-variant overrides — null means inherit from parent infra_item
  hsnCode?: string | null;
  gstRate?: number | null;
  unitPrice?: number | null;
  status: "Active" | "Discontinued";
  // No currentStock — infra assets are not quantity-tracked
}

const GST_RATES = [0, 5, 12, 18, 28] as const;

interface Props {
  index: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: (name: string, value: any) => void;
  onRemove: () => void;
  trailingAction?: React.ReactNode;
}

export function InfraCreateVariantRow({
  index,
  control,
  setValue,
  onRemove,
  trailingAction,
}: Props) {
  const attributeKeyId = useWatch({
    control,
    name: `variants.${index}.attributeKeyId`,
  });
  const attributeValueId = useWatch({
    control,
    name: `variants.${index}.attributeValueId`,
  });
  const hsnCode = useWatch({ control, name: `variants.${index}.hsnCode` });
  const gstRate = useWatch({ control, name: `variants.${index}.gstRate` });
  const unitPrice = useWatch({ control, name: `variants.${index}.unitPrice` });

  return (
    <div className="rounded border p-3 bg-muted/20 space-y-3">
      {/* Attribute key + value row */}
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Attribute Key</p>
          <InfraAttributeKeyCombobox
            value={attributeKeyId ?? null}
            onChange={(v) => {
              setValue(`variants.${index}.attributeKeyId`, v);
              setValue(`variants.${index}.attributeValueId`, null);
              setValue(`variants.${index}.attributeValueText`, "");
            }}
          />
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Attribute Value</p>
          <InfraAttributeValueCombobox
            attributeKeyId={attributeKeyId ?? null}
            value={attributeValueId ?? null}
            onChange={(id, text) => {
              setValue(`variants.${index}.attributeValueId`, id);
              if (text) setValue(`variants.${index}.attributeValueText`, text);
            }}
            disabled={attributeKeyId == null}
          />
        </div>
        <div className="flex flex-col gap-1 items-end">
          {trailingAction}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-destructive hover:text-destructive text-xs"
          >
            Remove
          </Button>
        </div>
      </div>

      {/* Per-variant HSN / GST / Price overrides */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <p className="text-xs text-muted-foreground mb-1">
            HSN Code{" "}
            <span className="text-muted-foreground/70">(optional)</span>
          </p>
          <Input
            placeholder="Inherits parent"
            value={hsnCode ?? ""}
            onChange={(e) =>
              setValue(
                `variants.${index}.hsnCode`,
                e.target.value === "" ? null : e.target.value,
              )
            }
          />
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">
            GST Rate{" "}
            <span className="text-muted-foreground/70">(optional)</span>
          </p>
          <Select
            value={gstRate != null ? String(gstRate) : "__inherit__"}
            onValueChange={(v) =>
              setValue(
                `variants.${index}.gstRate`,
                v === "__inherit__" ? null : Number(v),
              )
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Inherits parent" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__inherit__">Inherits parent</SelectItem>
              {GST_RATES.map((r) => (
                <SelectItem key={r} value={String(r)}>
                  {r}%
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">
            Unit Price (₹){" "}
            <span className="text-muted-foreground/70">(optional)</span>
          </p>
          <Input
            type="number"
            min={0}
            step="0.01"
            placeholder="Inherits parent"
            value={unitPrice ?? ""}
            onChange={(e) =>
              setValue(
                `variants.${index}.unitPrice`,
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
          />
        </div>
      </div>
    </div>
  );
}
